import React, { useState } from 'react';
import { McButton, McNotification, McIcon } from '@maersk-global/mds-react-wrapper';
import { ApiClient } from '../../services/apiClient';
import { REF_DATA_API_BASE } from '../../config/api';

const refDataClient = new ApiClient(REF_DATA_API_BASE);

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiPlaygroundProps {
  terminal: string;
  app: string;
  tableKey: string;
}

type SnippetLang = 'curl' | 'fetch' | 'axios';

interface EndpointDef {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  pathTemplate: string;
  summary: string;
  description: string;
  bodyTemplate?: Record<string, any>;
  /** Path params that need substituting in addition to terminal/app/tableKey */
  pathParams?: Record<string, string>;
}

// ─── Snippet generators ───────────────────────────────────────────────────────

function buildPath(template: string, vars: Record<string, string>) {
  return template.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

function toCurl(method: string, url: string, body?: any): string {
  const lines: string[] = [
    `curl -X ${method} \\`,
    `  "${url}" \\`,
    `  -H "Authorization: Bearer <your_token>" \\`,
    `  -H "Content-Type: application/json"`,
  ];
  if (body) {
    lines[lines.length - 1] += ' \\';
    lines.push(`  -d '${JSON.stringify(body, null, 2)}'`);
  }
  return lines.join('\n');
}

function toFetch(method: string, url: string, body?: any): string {
  const opts: Record<string, any> = {
    method,
    headers: {
      Authorization: 'Bearer <your_token>',
      'Content-Type': 'application/json',
    },
  };
  if (body) opts.body = 'JSON.stringify(payload)';
  const lines: string[] = [];
  if (body) lines.push(`const payload = ${JSON.stringify(body, null, 2)};\n`);
  lines.push(
    `const res = await fetch("${url}", ${JSON.stringify(opts, null, 2).replace('"JSON.stringify(payload)"', 'JSON.stringify(payload)')});`,
    `const data = await res.json();`,
  );
  return lines.join('\n');
}

function toAxios(method: string, url: string, body?: any): string {
  const m = method.toLowerCase();
  if (body) {
    return (
      `const payload = ${JSON.stringify(body, null, 2)};\n\n` +
      `const { data } = await axios.${m}(\n  "${url}",\n  payload,\n  { headers: { Authorization: "Bearer <your_token>" } }\n);`
    );
  }
  return `const { data } = await axios.${m}(\n  "${url}",\n  { headers: { Authorization: "Bearer <your_token>" } }\n);`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  section: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    marginBottom: '0.75rem',
    overflow: 'hidden' as const,
  },
  header: (open: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.65rem 0.875rem',
    cursor: 'pointer',
    backgroundColor: open ? '#f3f4f6' : '#ffffff',
    color: '#111827',
    userSelect: 'none',
    borderBottom: open ? '1px solid #e5e7eb' : 'none',
  }),
  badge: (method: string): React.CSSProperties => {
    const map: Record<string, string> = {
      GET: '#1565C0', POST: '#2E7D32', PATCH: '#E65100', PUT: '#6A1B9A', DELETE: '#b00020',
    };
    return {
      padding: '1px 7px',
      borderRadius: '3px',
      backgroundColor: map[method] ?? '#555',
      color: 'white',
      fontSize: '0.72rem',
      fontWeight: 700,
      fontFamily: 'monospace',
      minWidth: '50px',
      textAlign: 'center',
    };
  },
  path: {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    color: '#374151',
    flex: 1,
  } as React.CSSProperties,
  summary: {
    fontSize: '0.8rem',
    color: '#6B7280',
    marginLeft: 'auto',
  } as React.CSSProperties,
  body: {
    padding: '0.875rem',
    backgroundColor: '#ffffff',
    color: '#111827',
  },
  description: {
    fontSize: '0.82rem',
    color: '#4B5563',
    marginBottom: '0.75rem',
  },
  langTabs: {
    display: 'flex',
    gap: 0,
    marginBottom: '0.5rem',
    borderBottom: '1px solid #e5e7eb',
  } as React.CSSProperties,
  langTab: (active: boolean): React.CSSProperties => ({
    padding: '0.3rem 0.75rem',
    fontSize: '0.78rem',
    fontWeight: active ? 700 : 400,
    color: active ? '#0a6e82' : '#6B7280',
    borderBottom: active ? '2px solid #0a6e82' : '2px solid transparent',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    marginBottom: '-1px',
  }),
  pre: {
    backgroundColor: '#1e1e2e',
    color: '#cdd6f4',
    padding: '0.75rem',
    borderRadius: '5px',
    fontSize: '0.78rem',
    fontFamily: 'monospace',
    overflowX: 'auto' as const,
    whiteSpace: 'pre' as const,
    margin: '0.35rem 0',
  } as React.CSSProperties,
  responseBox: {
    backgroundColor: '#0d1117',
    color: '#8bc34a',
    padding: '0.75rem',
    borderRadius: '5px',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    overflowX: 'auto' as const,
    whiteSpace: 'pre' as const,
    marginTop: '0.5rem',
    maxHeight: '320px',
    overflowY: 'auto' as const,
  } as React.CSSProperties,
  tryRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    marginTop: '0.75rem',
  } as React.CSSProperties,
  copyBtn: {
    background: 'none',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '3px 8px',
    fontSize: '0.72rem',
    color: '#6B7280',
  } as React.CSSProperties,
};

// ─── Endpoint panel ───────────────────────────────────────────────────────────

function EndpointPanel({
  ep,
  vars,
}: {
  ep: EndpointDef;
  vars: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<SnippetLang>('curl');
  const [response, setResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const allVars = { ...vars, ...(ep.pathParams ?? {}) };
  const resolvedPath = buildPath(ep.pathTemplate, allVars);
  const fullUrl = `${REF_DATA_API_BASE}${resolvedPath}`;

  const snippet =
    lang === 'curl'
      ? toCurl(ep.method, fullUrl, ep.bodyTemplate)
      : lang === 'fetch'
      ? toFetch(ep.method, fullUrl, ep.bodyTemplate)
      : toAxios(ep.method, fullUrl, ep.bodyTemplate);

  async function tryIt() {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      let result: any;
      const path = resolvedPath;
      if (ep.method === 'GET') result = await refDataClient.get(path);
      else if (ep.method === 'POST') result = await refDataClient.post(path, ep.bodyTemplate ?? {});
      else if (ep.method === 'PATCH') result = await refDataClient.patch(path, ep.bodyTemplate ?? {});
      else if (ep.method === 'PUT') result = await refDataClient.put(path, ep.bodyTemplate ?? {});
      else if (ep.method === 'DELETE') result = await refDataClient.delete(path);
      setResponse(JSON.stringify(result, null, 2));
    } catch (e: any) {
      const status = e?.response?.status ?? e?.status ?? '';
      const data = e?.response?.data ?? e?.message ?? String(e);
      setError(`${status ? `HTTP ${status}: ` : ''}${typeof data === 'object' ? JSON.stringify(data, null, 2) : data}`);
    } finally {
      setLoading(false);
    }
  }

  function copySnippet() {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div style={s.section}>
      <div style={s.header(open)} onClick={() => setOpen(o => !o)}>
        <span style={s.badge(ep.method)}>{ep.method}</span>
        <span style={s.path}>{buildPath(ep.pathTemplate, allVars)}</span>
        <span style={s.summary}>{ep.summary}</span>
        <span style={{ fontSize: '0.8rem', color: 'var(--mds-color-neutral-40)', marginLeft: '0.5rem' }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {open && (
        <div style={s.body}>
          <p style={s.description}>{ep.description}</p>

          {/* Language tabs */}
          <div style={s.langTabs}>
            {(['curl', 'fetch', 'axios'] as SnippetLang[]).map(l => (
              <McButton
                key={l}
                fit="small"
                variant={lang === l ? 'filled' : 'plain'}
                appearance="neutral"
                onClick={() => setLang(l)}
              >
                {l === 'curl' ? 'cURL' : l === 'fetch' ? 'fetch' : 'axios'}
              </McButton>
            ))}
          </div>

          <pre style={s.pre}>{snippet}</pre>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <McButton fit="small" variant="outlined" onClick={copySnippet}>
              <McIcon icon={copied ? 'check' : 'copy'} slot="prefix" />
              {copied ? 'Copied' : 'Copy'}
            </McButton>
          </div>

          {/* Try It */}
          <div style={s.tryRow}>
            <McButton fit="small" onClick={tryIt} disabled={loading}>
              {loading ? 'Running…' : '▶ Try It'}
            </McButton>
            {response === null && !error && !loading && (
              <span style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-40)' }}>
                Runs against {REF_DATA_API_BASE} with your current session token
              </span>
            )}
          </div>

          {error && (
            <McNotification
              appearance="error"
              heading="Request failed"
              body={error.slice(0, 300)}
            />
          )}

          {response !== null && (
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-50)', marginTop: '0.5rem', marginBottom: '0.25rem' }}>
                Response
              </div>
              <pre style={s.responseBox}>{response}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ApiPlayground({ terminal, app, tableKey }: ApiPlaygroundProps) {
  // REF_DATA_API_BASE already includes /api/v1 — paths start from /{terminal}/...
  const schemaBase = `/{terminal}/{app}/schemas`;
  const rowBase = `/{terminal}/{app}/schemas/{tableKey}/rows`;

  const vars = { terminal, app, tableKey };

  const exampleColumns = [
    { key: 'code', displayName: 'Code', type: 'STRING', required: true },
    { key: 'name', displayName: 'Name', type: 'STRING', required: true },
    { key: 'is_active', displayName: 'Active', type: 'BOOLEAN' },
  ];

  const endpoints: EndpointDef[] = [
    {
      method: 'GET',
      pathTemplate: `${schemaBase}`,
      summary: 'List all schemas',
      description: `Returns every active schema registered for the terminal + application. Each schema describes its column definitions, version, and description.`,
    },
    {
      method: 'POST',
      pathTemplate: `${schemaBase}`,
      summary: 'Create a schema',
      description: `Register a new reference data table. Requires the Admin role. The tableKey must be unique per terminal+app and match ^[a-zA-Z0-9_]{1,63}$.`,
      bodyTemplate: {
        tableKey: tableKey || 'example_table',
        displayName: 'Example Table',
        description: 'Stores example reference data',
        columns: exampleColumns,
        containsPii: false,
      },
    },
    {
      method: 'PATCH',
      pathTemplate: `${schemaBase}/{tableKey}`,
      summary: 'Update schema',
      description: `Evolve the schema's column definitions. The version is incremented and the previous version is archived as an immutable snapshot. Requires the Admin role.`,
      bodyTemplate: {
        displayName: 'Example Table',
        description: 'Stores example reference data',
        columns: exampleColumns,
        containsPii: false,
      },
    },
    {
      method: 'GET',
      pathTemplate: `${schemaBase}/{tableKey}/history`,
      summary: 'Schema version history',
      description: `Lists every past schema version with its columns snapshot, change type, human-readable change summary, and the actor who made the change.`,
    },
    {
      method: 'GET',
      pathTemplate: `${rowBase}`,
      summary: 'List rows',
      description: `Returns rows for the selected table. Cursor pagination via ?cursor=<n>&limit=<n> (default 50, max 500); response is { items, nextCursor, hasMore }. Active rows only.`,
    },
    {
      method: 'POST',
      pathTemplate: `${rowBase}`,
      summary: 'Create row',
      description: `Insert a new row. The data object keys must match the column keys defined in the schema. Required fields, types, ENUM options and REF existence are validated server-side. Requires Write or Admin.`,
      bodyTemplate: {
        data: { code: 'EXAMPLE', name: 'Example Row', is_active: true },
      },
    },
    {
      method: 'PUT',
      pathTemplate: `${rowBase}/{rowId}`,
      summary: 'Update row',
      description: `Replace a row's data. Optimistic-locked — concurrent updates return 409. Requires Write or Admin.`,
      bodyTemplate: {
        data: { code: 'EXAMPLE', name: 'Updated Name', is_active: false },
      },
      pathParams: { rowId: '00000000-0000-0000-0000-000000000000' },
    },
    {
      method: 'DELETE',
      pathTemplate: `${rowBase}/{rowId}`,
      summary: 'Delete row',
      description: `Soft-deletes a row (sets active=false, records the actor and a DELETE snapshot). The row is never physically removed. Requires Write or Admin.`,
      pathParams: { rowId: '00000000-0000-0000-0000-000000000000' },
    },
    {
      method: 'GET',
      pathTemplate: `${rowBase}/{rowId}/history`,
      summary: 'Row audit history',
      description: `Returns every immutable snapshot recorded for a specific row — CREATE, UPDATE, and DELETE events. Each entry includes the version number, change type, actor, timestamp, change summary, and the full data payload at that point in time.`,
      pathParams: { rowId: '00000000-0000-0000-0000-000000000000' },
    },
    {
      method: 'POST',
      pathTemplate: `${rowBase}/bulk`,
      summary: 'Bulk replace',
      description: `Atomically replaces all active rows for this table in a single transaction (all existing rows soft-deleted, new ones inserted). Requires the Admin role; rate-limited.`,
      bodyTemplate: {
        rows: [
          { data: { code: 'ROW1', name: 'First Row', is_active: true } },
          { data: { code: 'ROW2', name: 'Second Row', is_active: true } },
        ],
      },
    },
  ];

  return (
    <div>
      {/* Auth note */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.6rem 0.875rem',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        marginBottom: '1rem',
        fontSize: '0.8rem',
        color: '#374151',
      }}>
        <span>🔐</span>
        <span>
          All endpoints require a valid Bearer token.{' '}
          <strong>Write operations</strong> require the{' '}
          <code style={{ backgroundColor: '#f3f4f6', padding: '1px 4px', borderRadius: '3px', color: '#111827' }}>
            APMT.CDH.SelfService.Admin
          </code>{' '}
          or{' '}
          <code style={{ backgroundColor: '#f3f4f6', padding: '1px 4px', borderRadius: '3px', color: '#111827' }}>
            APMT.CDH.SelfService.Editor
          </code>{' '}
          role. The "Try It" button uses your current session token automatically.
        </span>
      </div>

      {/* Current selection context */}
      <div style={{
        padding: '0.5rem 0.875rem',
        backgroundColor: '#e8f4f7',
        border: '1px solid #0a6e82',
        borderRadius: '6px',
        marginBottom: '1rem',
        fontSize: '0.78rem',
        fontFamily: 'monospace',
        color: '#0a6e82',
      }}>
        terminal=<strong>{terminal}</strong> · app=<strong>{app}</strong> · tableKey=<strong>{tableKey}</strong>
      </div>

      {endpoints.map((ep, i) => (
        <EndpointPanel key={i} ep={ep} vars={vars} />
      ))}
    </div>
  );
}
